/**
 * The staff app catalogue — one list, used by both the Team home cards and the
 * global staff navigation rail.
 *
 * These two surfaces answer the same question ("which apps can this person
 * open?") and previously would have answered it twice. Keeping the labels,
 * icons, accents and — most importantly — the capability checks in one place is
 * what stops a person seeing an app on one surface and not the other.
 *
 * Capability answers are never invented here. They come from the TS bootstrap
 * identity via tsIdentity.js: `tsVisiblePortions` for the department portions,
 * `canAccessManage` / `canAccessDesignGallery` for Ops and Designs, and the
 * server-set `can_chat` flag for the chat surfaces. This module only decides
 * ordering and presentation.
 */
import { Cake, Truck, Package, MessageCircle, ClipboardList, Wallet, Briefcase, Images, LayoutDashboard } from "lucide-react";

import { OPS_BASENAME, isOpsPath } from "@/lib/opsBase";
import { TEAM_APP_PATHS, teamHomePath } from "@/lib/teamHub";
import {
  canAccessDesignGallery,
  canAccessManage,
  filterPortionsByDepartments,
  loadTsDepartments,
  loadTsPortionUrls,
  tsVisiblePortions,
} from "@/lib/tsIdentity";

/**
 * Presentation for each app.
 *
 * `label` is a nav label, not the product name: at the Team page's 4-up width a
 * card title gets roughly 11 characters, and the rail pills are tighter still.
 * Salary's canonical name stays "Salary & Attendance" in tsIdentity.js, which is
 * what the backend routes, the PWA manifest and guard-sa-app.mjs call it.
 */
const CATALOGUE = {
  manage: {
    label: "Ops",
    path: OPS_BASENAME,
    icon: LayoutDashboard,
    accent: "#5B6472",
    sub: "Office console: orders, customers, finance",
    // A different router basename, so it needs a document load, not a client
    // route transition.
    hardNav: true,
  },
  kitchen: {
    label: "Kitchen",
    path: TEAM_APP_PATHS.kitchen,
    icon: Cake,
    accent: "#EE2677",
    sub: "Production: baker, froster, decorator",
  },
  driver: {
    label: "Delivery",
    path: TEAM_APP_PATHS.driver,
    icon: Truck,
    accent: "#B4791B",
    sub: "Delivery board & handoff",
  },
  inventory: {
    label: "Inventory",
    path: TEAM_APP_PATHS.inventory,
    icon: Package,
    accent: "#0F7B6C",
    sub: "Stock bins & inventory",
  },
  salary: {
    label: "Salary",
    path: TEAM_APP_PATHS.salary,
    icon: Wallet,
    accent: "#6D5BD0",
    sub: "Salary & attendance records",
  },
  we: {
    label: "Work Ethic",
    path: TEAM_APP_PATHS.we,
    icon: Briefcase,
    accent: "#2C6BB8",
    sub: "Team tasks, time & work ethic",
  },
  reports: {
    label: "Reports",
    path: TEAM_APP_PATHS.chat,
    icon: ClipboardList,
    accent: "#2F7D4F",
    sub: "WhatsApp chat report handoff",
  },
  tc: {
    label: "Chats",
    path: TEAM_APP_PATHS.tc,
    icon: MessageCircle,
    accent: "#2F7D4F",
    sub: "WhatsApp inbox",
  },
  gallery: {
    label: "Designs",
    path: TEAM_APP_PATHS.gallery,
    icon: Images,
    accent: "#C2417A",
    sub: "Cake design gallery",
  },
};

/** The home destination. Always reachable; never a back button. */
export const STAFF_APP_HOME = Object.freeze({
  key: "home",
  label: "Team",
  get path() {
    return teamHomePath();
  },
});

export function staffAppEntry(key) {
  const app = CATALOGUE[key];
  return app ? { key, ...app } : null;
}

/**
 * Can this identity open this app?
 *
 * Deliberately delegates rather than re-reading `can_kitchen` and friends:
 * duplicating the flag names here is exactly how a rail starts showing an app
 * the Team page hides.
 */
export function canAccessStaffApp(identity, key) {
  if (!identity || !key) return false;
  if (key === "home") return true;
  if (key === "manage") return canAccessManage(identity);
  if (key === "gallery") return canAccessDesignGallery(identity);
  // Chat Report and the TC inbox are the same server-side capability.
  if (key === "tc" || key === "reports") return Boolean(identity.can_chat);
  return tsVisiblePortions(identity).some((portion) => portion.key === key);
}

/**
 * The apps to offer this identity, in the order both surfaces present them.
 *
 * `currentKey` is appended when the person is *inside* an app that the list
 * would otherwise omit — Chat Report has no Team card, and a portion can be
 * narrowed away by the department picker — so the rail can always highlight
 * where you actually are. It is still capability-checked.
 */
export function staffAppsFor(identity, { departments, currentKey } = {}) {
  if (!identity) return [];

  const apps = [];
  const push = (key, path) => {
    const entry = staffAppEntry(key);
    if (entry) apps.push(path ? { ...entry, path } : entry);
  };

  if (canAccessManage(identity)) push("manage");

  const visible = tsVisiblePortions(identity, loadTsPortionUrls());
  const selected = departments === undefined ? loadTsDepartments() : departments;
  for (const portion of filterPortionsByDepartments(visible, selected, identity)) {
    // The portion's own path wins: it accounts for host and portion-URL config.
    push(portion.key, portion.path);
  }

  if (identity.can_chat) push("tc");
  if (canAccessDesignGallery(identity)) push("gallery");

  if (currentKey && currentKey !== "home" && !apps.some((a) => a.key === currentKey)) {
    if (canAccessStaffApp(identity, currentKey)) push(currentKey);
  }

  return apps;
}

/** Which app a path belongs to, or null off the staff app tree. */
export function staffAppKeyForPath(pathname = "") {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  if (isOpsPath(path)) return "manage";
  if (path === "/") return "home";
  for (const [key, app] of Object.entries(CATALOGUE)) {
    if (app.hardNav) continue;
    if (path === app.path || path.startsWith(`${app.path}/`)) return key;
  }
  return null;
}

/**
 * True when this document must not draw staff chrome of its own.
 *
 * Two ways in. Any iframe: TC and Chat Report are embedded by a parent that
 * already draws the rail, so a second one would sit inside the frame. And
 * `?embed=1`, the existing marker Chat Report uses for its embedded mode (see
 * TsPortionGate) — same reasoning, whether or not the frame is cross-origin
 * enough for the window check to be reliable.
 */
export function isEmbeddedStaffView(search = typeof window !== "undefined" ? window.location.search : "") {
  if (typeof window === "undefined") return true;
  try {
    if (window.parent && window.parent !== window) return true;
  } catch {
    // Cross-origin parent access threw, which itself means we are framed.
    return true;
  }
  return new URLSearchParams(search || "").get("embed") === "1";
}
